import type { AppRole } from "@/hooks/useUserRoles";
import type { PersonRoleTagMap } from "@/hooks/usePersonRoleTags";

export type MessagingAudience = "church" | "staff" | "volunteers" | "members" | "donors";

export const MESSAGING_AUDIENCES: Array<{
  value: MessagingAudience;
  label: string;
  description: string;
}> = [
  {
    value: "church",
    label: "Church-wide",
    description: "Anyone in the directory.",
  },
  {
    value: "staff",
    label: "Staff only",
    description: "Paid and operational staff.",
  },
  {
    value: "volunteers",
    label: "Volunteers",
    description: "Active serving volunteers.",
  },
  {
    value: "members",
    label: "Members",
    description: "Confirmed members.",
  },
  {
    value: "donors",
    label: "Donors",
    description: "People with giving history.",
  },
];

export const DEFAULT_MESSAGING_ROLE_SCOPES: Record<AppRole, MessagingAudience[]> = {
  admin: ["church"],
  finance: ["donors", "members"],
  // finance_admin/treasurer/bookkeeper/ap_clerk/ar_clerk/approver: added when AppRole
  // grew these roles -- without an entry here they silently resolved to zero
  // audiences. finance_admin and treasurer mirror `finance`'s scope because both are
  // in supabase/functions/_shared/messaging-permissions.ts's MESSAGING_SEND_ROLES, so
  // the send grant backs up the client-side audience. bookkeeper/ap_clerk/ar_clerk/
  // approver are NOT in MESSAGING_SEND_ROLES (view-only per ROLE_PERMISSIONS), so their
  // scope stays empty rather than advertising recipients the Edge Function will 403 on
  // send. The actual scope these roles should have is a product decision tracked in
  // #5722, not something inferable from the code alone.
  finance_admin: ["donors", "members"],
  treasurer: ["donors", "members"],
  bookkeeper: [],
  ap_clerk: [],
  ar_clerk: [],
  approver: [],
  pastor: ["church", "members", "volunteers"],
  youth: ["members", "volunteers"],
  volunteer_coordinator: ["volunteers", "staff"],
  facilities: ["staff"],
  staff: ["members", "volunteers", "staff"],
  ministry_leader: ["members", "volunteers", "staff"],
  volunteer: ["volunteers"],
  member: ["members"],
  auditor: [],
  super_admin: ["church"],
  platform_super_admin: ["church"],
  platform_support: [],
  platform_readonly: [],
};

export function getAllowedAudiencesForRoles(
  roleScopes: Record<AppRole, MessagingAudience[]> | undefined,
  roles: AppRole[]
): MessagingAudience[] {
  const scopes = roleScopes || DEFAULT_MESSAGING_ROLE_SCOPES;
  const allowed = new Set<MessagingAudience>();
  roles.forEach((role) => {
    scopes[role]?.forEach((scope) => allowed.add(scope));
  });
  return Array.from(allowed);
}

export function getAudienceLabel(audience: MessagingAudience): string {
  return MESSAGING_AUDIENCES.find((item) => item.value === audience)?.label ?? audience;
}

export function filterPeopleByAudience<T extends { id: string }>(
  people: T[],
  allowedAudiences: MessagingAudience[],
  roleTagMap: PersonRoleTagMap
): T[] {
  if (allowedAudiences.includes("church")) {
    return people;
  }
  return people.filter((person) => {
    const roles = roleTagMap[person.id];
    if (allowedAudiences.includes("staff") && roles?.has("Staff")) return true;
    if (allowedAudiences.includes("volunteers") && roles?.has("Volunteer")) return true;
    if (allowedAudiences.includes("members") && roles?.has("Member")) return true;
    if (allowedAudiences.includes("donors") && roles?.has("Donor")) return true;
    return false;
  });
}

export function describeAudienceAccess(allowedAudiences: MessagingAudience[]): string {
  if (allowedAudiences.length === 0) {
    return "No messaging audiences are enabled for your role.";
  }
  if (allowedAudiences.includes("church")) {
    return "You can message anyone in the directory.";
  }
  const labels = allowedAudiences.map(getAudienceLabel);
  return `You can message ${labels.join(", ")}.`;
}
